import { HTMLMotionProps, motion, AnimatePresence } from 'framer-motion';
// eslint-disable-next-line
import React, { FC, PropsWithChildren } from 'react';

import { VARIANTS } from 'utils/motion-variants';
import styled from 'styled-components';
import { mergeAnimationProps } from '../utils';

type Props = PropsWithChildren<{
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  order?: number;
  isVisible?: boolean;
  animatePresence?: boolean;
  fullHeight?: boolean;
}> &
  HTMLMotionProps<'div'>;

/**
 * Wapper for simplification of slide animation
 * @example
 * <SlideAnimation><SomeComponent></SlideAnimation>
 *
 * <SlideAnimation order={2}><SomeComponent></SlideAnimation>
 *
 * <SlideAnimation direction="down"><SomeComponent></SlideAnimation>
 */

const Content = styled(motion.div)<Props & { $fullHeight?: boolean }>`
  ${({ $fullHeight: fullHeight }) => `
    ${
      fullHeight &&
      `
        height: 100%;
      `
    }
  `}
`;

const SlideAnimation: FC<Props> = ({
  order = 1,
  direction = 'up',
  delay: initialDelay = 0,
  isVisible = true,
  exit,
  animatePresence = true,
  initial,
  animate,
  transition: initialTransition,
  children,
  fullHeight,
  ...rest
}) => {
  const delay = order ? order / 10 : initialDelay;
  const transition = {
    ...initialTransition,
    ...VARIANTS.transition.default,
    delay: order ? order / 10 : delay,
  };

  const showContent = () => {
    if (direction === 'right') {
      return (
        isVisible && (
          <Content
            $fullHeight={fullHeight}
            initial={mergeAnimationProps(
              initial,
              VARIANTS.initial.hiddenFromLeft
            )}
            animate={mergeAnimationProps(animate, VARIANTS.animate.visibleXY)}
            exit={mergeAnimationProps(exit, VARIANTS.exit.opacity)}
            transition={mergeAnimationProps(initialTransition, transition)}
            {...rest}
          >
            {children}
          </Content>
        )
      );
    }

    if (direction === 'left') {
      return (
        isVisible && (
          <Content
            $fullHeight={fullHeight}
            initial={mergeAnimationProps(
              initial,
              VARIANTS.initial.hiddenFromRight
            )}
            animate={mergeAnimationProps(animate, VARIANTS.animate.visibleXY)}
            exit={mergeAnimationProps(exit, VARIANTS.exit.opacity)}
            transition={mergeAnimationProps(initialTransition, transition)}
            {...rest}
          >
            {children}
          </Content>
        )
      );
    }

    if (direction === 'down') {
      return (
        isVisible && (
          <Content
            $fullHeight={fullHeight}
            initial={mergeAnimationProps(
              initial,
              VARIANTS.initial.hiddenFromTop
            )}
            animate={mergeAnimationProps(animate, VARIANTS.animate.visibleXY)}
            exit={mergeAnimationProps(exit, VARIANTS.exit.opacity)}
            transition={mergeAnimationProps(initialTransition, transition)}
            {...rest}
          >
            {children}
          </Content>
        )
      );
    }

    // default is "up"
    return (
      isVisible && (
        <Content
          $fullHeight={fullHeight}
          initial={mergeAnimationProps(
            initial,
            VARIANTS.initial.hiddenFromBottom
          )}
          animate={mergeAnimationProps(animate, VARIANTS.animate.visibleXY)}
          exit={mergeAnimationProps(exit, VARIANTS.exit.opacity)}
          transition={mergeAnimationProps(initialTransition, transition)}
          {...rest}
        >
          {children}
        </Content>
      )
    );
  };

  if (animatePresence) {
    return <AnimatePresence>{showContent()}</AnimatePresence>;
  }

  return <>{showContent()}</>;
};
export default SlideAnimation;
